import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  FieldArray,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import {
  emailWOValueValidator,
  panWOValueValidator,
  phoneValidator,
  requiredValidator,
  skipValidator,
} from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { getUserMById, updateUserM } from "./services/userManagement.services";
import { clearUserMDetails } from "./userManagementSlice";
import ShadowCard from "../../components/common/ShadowCard";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { TRAVELLER_GENDER } from "../../_contstants/common";
import FormDatePicker from "../../components/formFields/FormDateField";
import { findAllActiveNationality } from "../nationality/services/nationality.services";
import TravellerInfoArray from "./TravellerInfoArray";
import moment from "moment";
import { Country, State, City } from "country-state-city";
import i18nNationality from "i18n-nationality";
import en from "i18n-nationality/langs/en.json";
import FormCheckbox from "../../components/formFields/FormCheckbox";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
i18nNationality.registerLocale(en);

const CreateUserManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const ID = location.state?.ID;
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const nationality_list = i18nNationality.getNames("en");

  const [formKey, setFormKey] = React.useState(1);

  const loading = useAppSelector((state) => state.userM.loading);
  const UserDetails = useAppSelector((state) => state.userM.UserMDetails);

  useEffect(() => {
    if (ID) {
      dispatch(getUserMById(ID));
    }
  }, [ID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [UserDetails]);

  useEffect(() => {
    dispatch(findAllActiveNationality());
    return () => {
      dispatch(clearUserMDetails());
    };
  }, []);

  const handleSubmit = async (values: any) => {
    const formData = {
      ID: ID ? ID : 0,
      first_name: values.first_name ?? "",
      last_name: values.last_name ?? "",
      date_of_birth: values.date_of_birth
        ? moment(values.date_of_birth).format("YYYY-MM-DD")
        : null,
      gender: values.gender ?? 1,

      nationality_name: values.nationality_name ?? "",
      nationality_code: values.nationality_code ?? "",
      country_name: values.country_name ?? "",
      country_code: values.country_code ?? "",
      state_name: values.state_name ?? "",
      state_code: values.state_code ?? "",
      city_name: values.city_name ?? "",

      profile_photo: values.profile_photo,
      is_email_verified: values.is_email_verified,
      billing_address: values.billing_address,

      billing_country_name: values.billing_country_name ?? "",
      billing_country_code: values.billing_country_code ?? "",
      billing_state_name: values.billing_state_name ?? "",
      billing_state_code: values.billing_state_code ?? "",
      billing_city_name: values.billing_city_name ?? "",

      billing_pan_card_no: values.billing_pan_card_no,
      billing_pincode: values.billing_pincode,
      billing_gst_no: values.billing_gst_no,
      billing_gst_reg_name: values.billing_gst_reg_name,
      billing_pan_card_name: values.billing_pan_card_name,
      mobile_no: values.mobile_no,
      email_id: values.email_id,
      IsServiceCharge: values.IsServiceCharge || false,
      Domestic_Conv_Fee: values.IsServiceCharge
        ? values.Domestic_Conv_Fee || 0
        : 0,
      International_Conv_Fee: values.IsServiceCharge
        ? values.International_Conv_Fee || 0
        : 0,
      Domestic_Cancellation_Fee: values.IsServiceCharge
        ? values.Domestic_Cancellation_Fee || 0
        : 0,
      International_Cancellation_Fee: values.IsServiceCharge
        ? values.International_Cancellation_Fee || 0
        : 0,
      IsCreditLimit: values.IsCreditLimit || false,
      CreditLimit: values.IsCreditLimit ? values.CreditLimit || 0 : 0,
      CreditDays: values.IsCreditLimit ? values.CreditDays || 0 : 0,
      TravellersInformationList: values.TravellersInformationList.map(
        (tInfo: any) => {
          return {
            id: tInfo.id ?? 0,
            user_id: tInfo.user_id ?? ID,
            first_name: tInfo.first_name,
            last_name: tInfo.last_name,
            initial: tInfo.initial,
            gender: tInfo.initial === "MR" || tInfo.initial === "MSTR" ? 1 : 2,
            email_id: tInfo.email_id,
            mobile_no: tInfo.mobile_no,
            date_of_birth: tInfo.date_of_birth
              ? moment(tInfo.date_of_birth).format("YYYY-MM-DD")
              : null,
            pan_card_no: tInfo.pan_card_no,
            passport_no: tInfo.passport_no,
            // issue_country: tInfo.issue_country ?? 0,
            issue_country_name: tInfo.issue_country_name ?? "",
            issue_country_code: tInfo.issue_country_code ?? "",
            expire_date: tInfo.expire_date
              ? moment(tInfo.expire_date).format("YYYY-MM-DD")
              : null,
          };
        }
      ),
    };
    const response = await dispatch(updateUserM(formData));
    if (response?.meta?.requestStatus === "fulfilled") {
      navigate(-1);
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={UserDetails}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4>
                    {ID ? "Update User" : "Create User"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="first_name"
                    maxLength="100"
                    label="First Name"
                    placeholder="i.e John"
                    // astrike={true}
                    component={FormTextField}
                    // validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="last_name"
                    maxLength="100"
                    label="Last Name"
                    placeholder="i.e Doe"
                    component={FormTextField}
                    // astrike={true}
                    // validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name={`email_id`}
                    label="Email"
                    placeholder="i.e. example@gmail.com"
                    type="text"
                    component={FormTextField}
                    // astrike={true}
                    validator={emailWOValueValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name={`mobile_no`}
                    label="Mobile No"
                    placeholder="i.e. 9876543210"
                    type="text"
                    component={FormTextField}
                    astrike={true}
                    validator={phoneValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="date_of_birth"
                    maxLength="100"
                    label="Date of Birth"
                    // astrike={true}
                    component={FormDatePicker}
                    // validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="gender"
                    maxLength="100"
                    label="Gender"
                    component={FormSelectionField}
                    options={TRAVELLER_GENDER?.map((gen: any) => {
                      return {
                        value: gen?.value,
                        label: gen?.label,
                      };
                    })}
                    // validator={requiredValidator}
                    // astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="nationality_code"
                    maxLength="100"
                    label="Nationality"
                    placeholder="i.e. Indian"
                    component={FormSelectionField}
                    onChange={(e) => {
                      const code = e?.value;
                      const name = nationality_list[code] || "";
                      formRenderProps.onChange("nationality_name", {
                        value: name,
                      });
                    }}
                    options={(Object.keys(nationality_list) || [])?.map(
                      (code: any) => {
                        return {
                          value: code,
                          label: nationality_list[code],
                        };
                      }
                    )}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="country_code"
                    maxLength="100"
                    label="Country"
                    placeholder="i.e. India"
                    component={FormSelectionField}
                    onChange={(e) => {
                      const code = e?.value;
                      const selectedCountry = Country?.getAllCountries()?.find(
                        (country) => country?.isoCode === code
                      );
                      const name = selectedCountry ? selectedCountry?.name : "";

                      formRenderProps.onChange("country_name", {
                        value: name,
                      });
                    }}
                    options={Country.getAllCountries()?.map((item: any) => {
                      return {
                        value: item?.isoCode,
                        label: item?.name,
                      };
                    })}
                    // validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="state_code"
                    maxLength="100"
                    label="State"
                    placeholder="i.e. Gujarat"
                    component={FormSelectionField}
                    onChange={(e) => {
                      const code = e?.value;
                      const selectedState = State.getStatesOfCountry(
                        formRenderProps.valueGetter("country_code")
                      ).find((state) => state?.isoCode === code);
                      const name = selectedState ? selectedState?.name : "";
                      formRenderProps.onChange("state_name", {
                        value: name,
                      });
                    }}
                    options={State?.getStatesOfCountry(
                      formRenderProps.valueGetter("country_code")
                    )?.map((item: any) => {
                      return {
                        value: item?.isoCode,
                        label: item?.name,
                      };
                    })}
                    // validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="city_name"
                    maxLength="100"
                    label="City"
                    placeholder="i.e. Ahmedabad"
                    component={FormSelectionField}
                    options={City.getCitiesOfState(
                      formRenderProps.valueGetter("country_code"),
                      formRenderProps.valueGetter("state_code")
                    )?.map((item: any) => {
                      return {
                        value: item?.name,
                        label: item?.name,
                      };
                    })}
                    // validator={requiredValidator}
                  />
                </GridLayoutItem>

                {/* <GridLayoutItem>
                  <Field
                    name="isactive"
                    label="Status"
                    astrike={true}
                    component={FormSelectionField}
                    validator={requiredValidator}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem> */}
              </GridLayout>
            </ShadowCard>
            <ShadowCard style={{ padding: 12, marginTop: 10 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4>Billing Details</Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="billing_country_code"
                    maxLength="100"
                    label="Billing Country"
                    placeholder="i.e. India"
                    component={FormSelectionField}
                    onChange={(e) => {
                      const code = e?.value;
                      const selectedCountry = Country?.getAllCountries()?.find(
                        (country) => country?.isoCode === code
                      );
                      const name = selectedCountry ? selectedCountry?.name : "";

                      formRenderProps.onChange("billing_country_name", {
                        value: name,
                      });
                    }}
                    options={Country.getAllCountries()?.map((item: any) => {
                      return {
                        value: item?.isoCode,
                        label: item?.name,
                      };
                    })}
                    // validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="billing_state_code"
                    maxLength="100"
                    label="Billing State"
                    placeholder="i.e. Gujarat"
                    component={FormSelectionField}
                    onChange={(e) => {
                      const code = e?.value;
                      const selectedState = State.getStatesOfCountry(
                        formRenderProps.valueGetter("billing_country_code")
                      ).find((state) => state?.isoCode === code);
                      const name = selectedState ? selectedState?.name : "";
                      formRenderProps.onChange("billing_state_name", {
                        value: name,
                      });
                    }}
                    options={State?.getStatesOfCountry(
                      formRenderProps.valueGetter("billing_country_code")
                    )?.map((item: any) => {
                      return {
                        value: item?.isoCode,
                        label: item?.name,
                      };
                    })}
                    // validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="billing_city_name"
                    maxLength="100"
                    label="Billing City"
                    placeholder="i.e. Ahmedabad"
                    component={FormSelectionField}
                    options={City.getCitiesOfState(
                      formRenderProps.valueGetter("billing_country_code"),
                      formRenderProps.valueGetter("billing_state_code")
                    )?.map((item: any) => {
                      return {
                        value: item?.name,
                        label: item?.name,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="billing_gst_reg_name"
                    type="text"
                    label="Registered Name"
                    placeholder="Billing Reg. Name"
                    component={FormTextField}
                    // validator={requiredValidator}
                    // astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="billing_pan_card_no"
                    type="text"
                    label="Billing PAN No"
                    placeholder="i.e ABCDE1234F"
                    component={FormTextField}
                    validator={panWOValueValidator}
                    // astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="billing_pan_card_name"
                    type="text"
                    label="Billing PAN Name"
                    placeholder="i.e John Doe"
                    component={FormTextField}
                    validator={
                      formRenderProps.valueGetter("billing_pan_card_no")
                        ? requiredValidator
                        : skipValidator
                    }
                    // astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="billing_pincode"
                    type="text"
                    placeholder="i.e 382408"
                    label="Billing Pincode"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="billing_gst_no"
                    type="text"
                    label="Billing GST NO"
                    placeholder="i.e 1234567890A1Z2"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="billing_address"
                    type="text"
                    label="Billing Address"
                    placeholder="Billing Address"
                    component={FormTextField}
                    // validator={requiredValidator}
                  />
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
            <ShadowCard style={{ marginTop: 10 }}>
              <GridLayoutItem colSpan={6}>
                <Typography.h4>{"Travellers Details"}</Typography.h4>
              </GridLayoutItem>
              <FieldArray
                formRenderProps={formRenderProps}
                component={TravellerInfoArray}
                name="TravellersInformationList"
              />
            </ShadowCard>
            <ShadowCard style={{ padding: 12, marginTop: 10 }}>
              <GridLayout
                style={{ marginRight: 10 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[{ width: "50%" }, { width: "50%" }]}
              >
                <GridLayoutItem style={{ width: "100%" }}>
                  <Field
                    name="IsServiceCharge"
                    label="Service Charge"
                    component={FormCheckbox}
                    wrapperStyle={{
                      borderRadius: "5px",
                      fontWeight: "bold",
                      fontSize: "16px",
                      padding: "10px",
                      background: "#e0e0e0",
                    }}
                  />
                </GridLayoutItem>
                <GridLayoutItem></GridLayoutItem>
                {formRenderProps.valueGetter("IsServiceCharge") && (
                  <>
                    <GridLayoutItem>
                      <Field
                        name="Domestic_Conv_Fee"
                        maxLength="100"
                        label="Domestic Conv Fee"
                        placeholder="i.e 250"
                        component={FormNumericTextField}
                        astrike={true}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="International_Conv_Fee"
                        maxLength="100"
                        label="International Conv Fee"
                        placeholder="i.e 500"
                        component={FormNumericTextField}
                        astrike={true}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="Domestic_Cancellation_Fee"
                        maxLength="100"
                        label="Domestic Cancellation Fee"
                        placeholder="i.e 200"
                        component={FormNumericTextField}
                        astrike={true}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="International_Cancellation_Fee"
                        maxLength="100"
                        label="International Cancellation Fee"
                        placeholder="i.e 500"
                        component={FormNumericTextField}
                        astrike={true}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                  </>
                )}
                <GridLayoutItem style={{ width: "100%" }}>
                  <Field
                    name="IsCreditLimit"
                    label="Credit Limit"
                    component={FormCheckbox}
                    wrapperStyle={{
                      borderRadius: "5px",
                      fontWeight: "bold",
                      fontSize: "16px",
                      padding: "10px",
                      background: "#e0e0e0",
                    }}
                  />
                </GridLayoutItem>
                <GridLayoutItem></GridLayoutItem>
                {formRenderProps.valueGetter("IsCreditLimit") && (
                  <>
                    <GridLayoutItem>
                      <Field
                        name="CreditLimit"
                        maxLength="100"
                        label="Credit Limit"
                        placeholder="i.e 1,00,000"
                        component={FormNumericTextField}
                        astrike={true}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="CreditDays"
                        maxLength="100"
                        label="Credit Days"
                        placeholder="i.e 7"
                        component={FormNumericTextField}
                        astrike={true}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                  </>
                )}
                <GridLayoutItem
                  colSpan={2}
                  style={{
                    textAlign: "end",
                    marginTop: "20px",
                    width: "100%",
                  }}
                >
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    style={{ marginRight: 4 }}
                    onClick={() => navigate("/usermanage")}
                  >
                    Cancel
                  </Button>
                  <ButtonWithLoading
                    label={ID ? "Update" : "Create"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateUserManagement;
